import {streamURL} from '../shared/stream-url.js';
import {isPlaylistURL} from '../shared/playlist.js';
import {id3Title} from '../shared/stream-title.js';
import {EQ_FREQUENCIES,EQ_PRESETS,normalizeEqualizer} from '../shared/equalizer.js';
export {EQ_PRESETS} from '../shared/equalizer.js';

export async function canProcessStream(url,{fetcher=globalThis.fetch,signal,timeout=2500}={}){
  const controller=new AbortController(),abort=()=>controller.abort();
  if(signal?.aborted)return false;
  signal?.addEventListener('abort',abort,{once:true});const timer=setTimeout(abort,timeout);
  try{
    let response=await fetcher(url,{method:'HEAD',mode:'cors',signal:controller.signal});
    if(response.status===405||response.status===501){
      response=await fetcher(url,{method:'GET',mode:'cors',signal:controller.signal});
      try{await response.body?.cancel();}catch{}
    }
    return response.ok;
  }catch{return false;}finally{clearTimeout(timer);signal?.removeEventListener('abort',abort);}
}

// Reconnect delays after a live stream drops, and how long playback may sit buffering before reconnecting.
const RETRY_DELAYS=[1000,2000,4000,8000,15000,30000],STALL_TIMEOUT=12000;

export class RadioEngine{
  constructor(station,{onStatus=()=>{},onMessage=()=>{},onPlaying=()=>{},onTitle=()=>{},volume=.7,equalizer={enabled:false,preset:'Flat'},AudioClass=globalThis.Audio,AudioContextClass=globalThis.AudioContext||globalThis.webkitAudioContext,fetcher=globalThis.fetch,loadHls=()=>import('hls.js'),production=Boolean(import.meta.env?.PROD),host=globalThis}={}){
    Object.assign(this,{station,onMessage,onPlaying,onTitle,volume,equalizer,AudioClass,AudioContextClass,fetcher,loadHls,production,host});
    this.onStatus=status=>{this.status=status;onStatus(status);};
    this.controller=new AbortController();this.audio=this.makeAudio();this.disposed=false;this.desired=false;this.prepared=false;this.played=false;this.retries=0;
    // Back online after a network change or sleep: reconnect now instead of waiting for the next retry.
    this.onOnline=()=>{if(this.prepared&&this.desired&&this.status!=='playing'){this.retries=0;void this.reload();}};
    host?.addEventListener?.('online',this.onOnline);
  }
  makeAudio(cors=false){
    const audio=new this.AudioClass();audio.volume=this.volume;audio.preload='none';if(cors)audio.crossOrigin='anonymous';
    const current=()=>!this.disposed&&this.audio===audio;
    audio.addEventListener('playing',()=>{if(current()){this.clearStall();this.played=true;this.retries=0;this.onStatus('playing');this.onPlaying(this.station);}});
    audio.addEventListener('pause',()=>{if(current()&&!this.reloading)this.onStatus('paused');});
    audio.addEventListener('waiting',()=>{if(current()&&this.desired){this.onStatus('loading');this.watchStall();}});
    // A live stream only ends when the connection closes, so reconnect while the listener wants audio.
    audio.addEventListener('ended',()=>{if(current()){if(this.desired&&this.played)this.recover();else{this.desired=false;this.onStatus('paused');}}});
    audio.addEventListener('error',()=>{if(current()&&!this.reloading)this.streamError();});
    return audio;
  }
  // Plain playback is tried first when the Web Audio (CORS) path may be to blame; it opens a fresh connection too.
  // Otherwise a stream that has played dropped its connection and reconnects, and one that never played is unavailable.
  streamError(){
    if(this.processing&&!this.fallbackAttempted)void this.fallback();
    else if(this.desired&&this.played)this.recover();
    else this.fail();
  }
  recover(){
    if(this.disposed||!this.desired||this.retryTimer)return;
    this.clearStall();
    if(this.retries>=RETRY_DELAYS.length){this.fail();return;}
    this.onStatus('loading');
    this.retryTimer=setTimeout(()=>{this.retryTimer=null;void this.reload();},RETRY_DELAYS[this.retries++]);
  }
  // Reconnects the same audio element (keeping the equalizer graph attached) to a fresh stream at the live edge.
  async reload(){
    clearTimeout(this.retryTimer);this.retryTimer=null;this.clearStall();
    if(this.disposed||!this.desired)return;
    const audio=this.audio;this.reloading=true;this.onStatus('loading');
    try{
      this.hls?.destroy();this.hls=null;audio.removeAttribute('src');audio.load();this.resolved=null;
      await this.attach(audio);if(this.disposed||!this.desired||this.audio!==audio)return;
      if(this.context&&this.context.state!=='running')try{await this.context.resume();}catch{}
      if(this.context&&this.context.state!=='running'){this.reloading=false;await this.fallback();return;}
      this.reloading=false;await audio.play();
    }catch(error){
      this.reloading=false;
      if(this.disposed||!this.desired||this.audio!==audio)return;
      if(error.name==='NotAllowedError'){this.onStatus('paused');this.onMessage('Your browser blocked automatic playback. Press play to resume.');}
      else if(this.played)this.recover();
      else this.fail();
    }finally{if(this.audio===audio)this.reloading=false;}
  }
  watchStall(){
    if(!this.played||this.stallTimer)return;
    const audio=this.audio,time=audio.currentTime;
    this.stallTimer=setTimeout(()=>{this.stallTimer=null;if(this.audio===audio&&this.desired&&audio.currentTime===time)this.recover();},STALL_TIMEOUT);
  }
  clearStall(){clearTimeout(this.stallTimer);this.stallTimer=null;}
  setVolume(volume){this.volume=volume;if(this.audio)this.audio.volume=volume;}
  setEqualizer(equalizer){
    this.equalizer=normalizeEqualizer(equalizer);const values=this.equalizer.enabled?this.equalizer.bands:EQ_PRESETS.Flat;
    this.graph?.filters.forEach((filter,i)=>filter.gain.setTargetAtTime(values[i],this.context.currentTime,.08));
    if(this.prepared&&!this.graph)this.onMessage(equalizer.enabled?'Equalizer unavailable for this stream. Standard playback is available.':'');
  }
  closeGraph(){
    this.graph=null;this.processing=false;
    if(this.context){const context=this.context;this.context=null;try{void Promise.resolve(context.close()).catch(()=>{});}catch{}}
  }
  // Playlist files (.m3u, .pls...) are read by the server, which returns the stream they list.
  // Other streams are returned directly, so playback starts without an extra await.
  streamSource(){
    const url=streamURL(this.station,{production:this.production});
    if(!isPlaylistURL(url))return {url,hls:Number(this.station.hls)===1||url.includes('.m3u8')};
    const fetcher=this.fetcher;// Called unbound: window.fetch rejects any other `this`.
    this.resolved??=(async()=>{
      const response=await fetcher(`/api/stations/${encodeURIComponent(this.station.stationuuid)}/stream`,{credentials:'same-origin',signal:this.controller.signal});
      if(!response.ok)throw new Error('Playlist unavailable');
      const stream=await response.json();
      return {url:streamURL({url:stream.url},{production:this.production}),hls:Boolean(stream.hls)};
    })().catch(error=>{this.resolved=null;throw error;});
    return this.resolved;
  }
  async attach(audio){
    let source=this.streamSource();
    if(source instanceof Promise){source=await source;if(this.disposed||this.audio!==audio)return;}
    const {url,hls}=source;
    // hls.js first: Chrome now reports native HLS ("maybe") but fails on some streams that hls.js
    // plays (MPEG-TS AAC segments end in MediaError 4). Native HLS is the fallback without MSE (iPhone).
    // HLS titles arrive as ID3 tags in the segments, not from the server (see usesHls).
    this.usesHls=Boolean(hls);
    if(hls){
      const {default:Hls}=await this.loadHls();if(this.disposed||this.audio!==audio)return;
      if(!Hls.isSupported()){
        if(!audio.canPlayType('application/vnd.apple.mpegurl'))throw new Error('Unsupported HLS format');
        audio.src=url;return;
      }
      this.hls=new Hls();this.hls.loadSource(url);this.hls.attachMedia(audio);
      this.hls.on(Hls.Events.FRAG_PARSING_METADATA,(_,data)=>{if(this.disposed||this.audio!==audio)return;for(const sample of data?.samples||[]){const tag=id3Title(sample.data);if(tag)this.onTitle(tag);}});
      this.hls.on(Hls.Events.ERROR,(_,data)=>{if(!this.disposed&&this.audio===audio&&data.fatal&&!this.reloading)this.streamError();});
    }else audio.src=url;
  }
  async prepare(){
    // Construct/resume during the play gesture, before the asynchronous playlist and CORS checks.
    if(this.AudioContextClass)try{this.context=new this.AudioContextClass();void Promise.resolve(this.context.resume()).catch(()=>{});}catch{this.context=null;}
    let source=this.streamSource();
    if(source instanceof Promise){source=await source;if(this.disposed)return;}
    const {url}=source;
    const allowed=Boolean(this.context)&&await canProcessStream(url,{fetcher:this.fetcher,signal:this.controller.signal});
    if(this.disposed)return;
    // A suspended Web Audio graph can silence media that is allowed to autoplay.
    // Keep startup on the normal audio path until audio processing is permitted.
    if(allowed&&this.context?.state==='running'){
      this.audio.crossOrigin='anonymous';
      try{
        const source=this.context.createMediaElementSource(this.audio);
        const filters=EQ_FREQUENCIES.map(frequency=>{const filter=this.context.createBiquadFilter();filter.type='peaking';filter.frequency.value=frequency;filter.Q.value=1;return filter;});
        let previous=source;for(const filter of filters){previous.connect(filter);previous=filter;}previous.connect(this.context.destination);
        this.graph={filters};this.processing=true;this.setEqualizer(this.equalizer);
      }catch{
        this.closeGraph();this.audio=this.makeAudio();
      }
    }else{this.closeGraph();}
    await this.attach(this.audio);if(this.disposed)return;this.prepared=true;
    if(!this.graph&&this.equalizer.enabled)this.onMessage('Equalizer unavailable for this stream. Standard playback is available.');
  }
  play(){
    if(this.disposed)return Promise.resolve();this.desired=true;
    // Resume inside the click: browsers suspend Web Audio after sleep or an output device change.
    if(this.context?.state==='suspended')try{void Promise.resolve(this.context.resume()).catch(()=>{});}catch{}
    if(this.playTask)return this.playTask;
    if(this.prepared&&!this.audio.paused&&this.status==='playing')return Promise.resolve();
    this.onStatus('loading');this.onMessage('');
    this.playTask=this.startPlayback().finally(()=>{this.playTask=null;});return this.playTask;
  }
  async startPlayback(){
    try{
      // Playing again after a pause, error or dropped connection opens a fresh connection: the old one
      // may be dead, and a live stream should resume at the live edge, not from a stale buffer.
      if(this.prepared){this.retries=0;await this.reload();return;}
      await this.prepare();
      if(this.disposed||!this.desired)return;
      if(this.context&&this.context.state!=='running'){await this.fallback();return;}
      await this.audio.play();
    }catch(error){
      if(this.disposed||!this.desired)return;
      if(error.name==='NotAllowedError'){this.onStatus('paused');this.onMessage('Your browser blocked automatic playback. Press play to resume.');}
      else if(this.fallbackTask)await this.fallbackTask;
      else if(this.processing&&!this.fallbackAttempted)await this.fallback();
      else this.fail();
    }
  }
  fallback(){
    if(this.fallbackTask)return this.fallbackTask;
    this.fallbackAttempted=true;this.closeGraph();
    this.onMessage(this.equalizer.enabled?'Equalizer unavailable for this stream. Using standard playback.':'');
    const previous=this.audio;this.audio=this.makeAudio();previous.pause();previous.removeAttribute('src');previous.load();this.hls?.destroy();this.hls=null;
    this.fallbackTask=(async()=>{try{await this.attach(this.audio);this.prepared=true;if(this.desired&&!this.disposed)await this.audio.play();}catch(error){if(this.disposed||!this.desired)return;if(error.name==='NotAllowedError'){this.onStatus('paused');this.onMessage('Your browser blocked automatic playback. Press play to resume.');}else if(this.played)this.recover();else this.fail();}})();
    return this.fallbackTask;
  }
  pause(){this.desired=false;clearTimeout(this.retryTimer);this.retryTimer=null;this.clearStall();this.audio.pause();this.onStatus('paused');}
  fail(){if(!this.disposed){this.onStatus('error');this.onMessage('This stream is unavailable or blocked by your browser. Try another station.');}}
  destroy(){
    this.disposed=true;this.desired=false;this.controller.abort();clearTimeout(this.retryTimer);this.clearStall();this.host?.removeEventListener?.('online',this.onOnline);this.audio.pause();this.audio.removeAttribute('src');this.audio.load();this.hls?.destroy();this.closeGraph();
  }
}
